import { redirect } from "react-router";
import browserPaths from "@/details/navigation/browser/browserPaths";
import type { FeaturePermissionKey } from "@/app/shared/permissions/featurePermissions";
import { canAny, hasAnyRoleLabel } from "@/app/shared/permissions/permissionKernel";
import { trackUnauthorizedRouteAccess } from "@/app/shared/permissions/permissionTelemetry";

export function getDashboardPermissionDeniedPath(): string {
  const p = browserPaths.DASHBOARD.ERROR.UNAUTHORIZED;
  return p.startsWith("/") ? p : `/${p}`;
}

export function getModuleInactivePath(): string {
  const p = browserPaths.DASHBOARD.ERROR.MODULE_INACTIVE;
  return p.startsWith("/") ? p : `/${p}`;
}

export type DashboardRouteAccessContext = {
  /** Relative dashboard path segment (e.g. `commercial`) for telemetry. */
  routeId: string;
};

/**
 * Returns a redirect to the dashboard permission-denied page when the user
 * lacks any of the required feature permissions; otherwise null (allow).
 */
export function assertDashboardRouteAccess(
  required: readonly FeaturePermissionKey[] | null | undefined,
  telemetryContext?: DashboardRouteAccessContext,
): ReturnType<typeof redirect> | null {
  if (!required?.length) return null;
  if (canAny(required)) return null;
  if (telemetryContext) {
    trackUnauthorizedRouteAccess({
      routeId: telemetryContext.routeId,
      featureKeys: [...required],
    });
  }
  return redirect(getDashboardPermissionDeniedPath());
}

/**
 * Blocks a route gated behind a module toggle (e.g. KYB) when that module is
 * disabled. Distinct from a permission denial: the user may well be allowed
 * in, the feature itself just isn't active.
 */
export function assertDashboardModuleAccess(
  moduleEnabled: boolean,
  telemetryContext?: DashboardRouteAccessContext,
): ReturnType<typeof redirect> | null {
  if (moduleEnabled) return null;
  if (telemetryContext) {
    trackUnauthorizedRouteAccess({
      routeId: telemetryContext.routeId,
      featureKeys: ["MODULE_DISABLED:KYB"],
    });
  }
  return redirect(getModuleInactivePath());
}

/**
 * Same as assertDashboardRouteAccess, but for routes reserved to a raw role
 * name (BOX_AFRICA_ADMIN) instead of an RBAC permission tuple.
 */
export function assertDashboardRouteRole(
  requiredRoleLabels: readonly string[] | null | undefined,
  telemetryContext?: DashboardRouteAccessContext,
): ReturnType<typeof redirect> | null {
  if (!requiredRoleLabels?.length) return null;
  if (hasAnyRoleLabel(requiredRoleLabels)) return null;
  if (telemetryContext) {
    trackUnauthorizedRouteAccess({
      routeId: telemetryContext.routeId,
      featureKeys: [...requiredRoleLabels],
    });
  }
  return redirect(getDashboardPermissionDeniedPath());
}
